import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import PermissionsService from "../../../../services/permissions";
import { SuiModal } from "../../../common/suiModal";
import { usePermissionsContext } from "../../index/context";
import PermissionDetailBody from "../permission-detail-body";

const permissionsService = new PermissionsService();

const DeletePermissionModal = ({ isOpen, onClose, permission }) => {
  const intl = useIntl();
  const [isDeleting, setIsDeleting] = useState(false);
  const { setSuccessDeleteMessage, setErrorDeleteMessage } = usePermissionsContext();

  const buttons = useMemo(
    () => [
      {
        text: intl.formatMessage({ defaultMessage: "Cancel", id: "BTN_CANCEL" }),
        color: "secondary",
        onClick: onClose,
      },
      {
        text: intl.formatMessage({ defaultMessage: "Delete Permission", id: "BTN_DELETE_PERMISSION" }),
        color: "primary",
        onClick: () => {
          handleDeletePermission(permission.id);
        },
        props: {
          loading: isDeleting,
          disabled: isDeleting,
          "data-testid": "pcp-confirm-delete",
        },
      },
    ],
    [permission.id, intl, onClose, isDeleting],
  );

  const handleDeletePermission = async (id) => {
    try {
      setIsDeleting(true);
      await permissionsService.deletePermission(id);
      setSuccessDeleteMessage(
        intl.formatMessage({
          id: "MESSAGE_PERMISSION_DELETED",
          defaultMessage: "Permission deleted successfully",
        }),
      );
    } catch (e) {
      setErrorDeleteMessage(
        e?.error?.message ||
          intl.formatMessage({
            id: "ERROR_PERMISSION_DELETED_FALLBACK",
            defaultMessage: "An error occurred while deleting the permission.",
          }),
      );
    } finally {
      setIsDeleting(false);
      onClose();
    }
  };

  return (
    <SuiModal
      modalHeader={intl.formatMessage({
        defaultMessage: "Delete Permission",
        id: "HEADING_DELETE_PERMISSION",
      })}
      isOpen={isOpen}
      onClose={onClose}
      modalButtons={buttons}
      modalContent={
        <div>
          <p>
            {intl.formatMessage({
              defaultMessage: "Are you sure you want to delete this permission?",
              id: "BODY_CONFIRM_DELETE_PERMISSION",
            })}
          </p>
          <PermissionDetailBody permission={permission} />
        </div>
      }
    />
  );
};

export default DeletePermissionModal;

DeletePermissionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  permission: PropTypes.object.isRequired,
};
